import { Theme, ThemeMode } from '../types';

// Storage key for theme preference
const THEME_STORAGE_KEY = 'resynthLab_theme';

// Light theme definition
const lightTheme: Theme = {
    mode: 'light',
    colors: {
        // Background colors
        background: '#FFFFFF',
        backgroundSecondary: '#F9FAFB',
        backgroundTertiary: '#F3F4F6',
        
        // Text colors
        text: '#111827',
        textSecondary: '#374151',
        textMuted: '#6B7280',
        
        // Border colors
        border: '#E5E7EB',
        borderLight: '#F3F4F6',
        
        // Interactive colors
        primary: '#3B82F6',
        primaryHover: '#2563EB',
        secondary: '#8B5CF6',
        secondaryHover: '#7C3AED',
        
        // Status colors
        success: '#10B981',
        warning: '#F59E0B',
        error: '#EF4444',
        info: '#06B6D4',
        
        // Component specific
        cardBackground: '#FFFFFF',
        modalBackground: '#FFFFFF',
        modalOverlay: 'rgba(0, 0, 0, 0.5)',
        inputBackground: '#FFFFFF',
        buttonBackground: '#3B82F6',
        buttonText: '#FFFFFF',
        
        // Shadows
        shadowSm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    }
};

// Dark theme definition
const darkTheme: Theme = {
    mode: 'dark',
    colors: {
        // Background colors
        background: '#0F172A',
        backgroundSecondary: '#1E293B',
        backgroundTertiary: '#334155',
        
        // Text colors
        text: '#F1F5F9',
        textSecondary: '#CBD5E1',
        textMuted: '#94A3B8',
        
        // Border colors
        border: '#334155',
        borderLight: '#475569',
        
        // Interactive colors
        primary: '#60A5FA',
        primaryHover: '#3B82F6',
        secondary: '#A78BFA',
        secondaryHover: '#8B5CF6',
        
        // Status colors
        success: '#34D399',
        warning: '#FBBF24',
        error: '#F87171',
        info: '#22D3EE',
        
        // Component specific
        cardBackground: '#1E293B',
        modalBackground: '#1E293B',
        modalOverlay: 'rgba(0, 0, 0, 0.75)',
        inputBackground: '#334155',
        buttonBackground: '#3B82F6',
        buttonText: '#FFFFFF',
        
        // Shadows
        shadowSm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
        shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
        shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.4)'
    }
};

class ThemeService {
    private currentTheme: Theme = lightTheme;
    private themeMode: ThemeMode = 'auto';
    private systemPreference: 'light' | 'dark' = 'light';
    private themeChangeListeners: ((theme: Theme) => void)[] = [];
    private mediaQueryList: MediaQueryList | null = null;

    constructor() {
        // Check system preference
        if (typeof window !== 'undefined' && window.matchMedia) {
            this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
            this.systemPreference = this.mediaQueryList.matches ? 'dark' : 'light';
            
            // Listen for system preference changes
            this.mediaQueryList.addEventListener('change', (e) => {
                this.systemPreference = e.matches ? 'dark' : 'light';
                this.updateCurrentTheme();
            });
        }

        // Load saved theme preference
        this.loadThemePreference();
        this.updateCurrentTheme();
    }

    private loadThemePreference(): void {
        try {
            const saved = localStorage.getItem(THEME_STORAGE_KEY);
            if (saved && (saved === 'light' || saved === 'dark' || saved === 'auto')) {
                this.themeMode = saved as ThemeMode;
            }
        } catch (error) {
            console.error('Failed to load theme preference:', error);
        }
    }

    private saveThemePreference(mode: ThemeMode): void {
        try {
            localStorage.setItem(THEME_STORAGE_KEY, mode);
        } catch (error) {
            console.error('Failed to save theme preference:', error);
        }
    }

    private updateCurrentTheme(): void {
        const effectiveMode = this.themeMode === 'auto' ? this.systemPreference : this.themeMode;
        const newTheme = effectiveMode === 'dark' ? darkTheme : lightTheme;
        
        // Only notify if theme actually changed
        if (newTheme.mode !== this.currentTheme.mode) {
            this.currentTheme = newTheme;
            this.applyThemeToDocument();
            this.notifyThemeChange();
        } else {
            this.currentTheme = newTheme;
        }
    }

    private applyThemeToDocument(): void {
        if (typeof document === 'undefined') return;
        
        // Apply theme class to document root
        document.documentElement.classList.remove('theme-light', 'theme-dark');
        document.documentElement.classList.add(`theme-${this.currentTheme.mode}`);
        
        // Update meta theme-color for mobile browsers
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
            metaThemeColor.setAttribute('content', this.currentTheme.colors.background);
        }
    }

    private notifyThemeChange(): void {
        this.themeChangeListeners.forEach(listener => listener(this.currentTheme));
    }

    // Public methods
    
    setTheme(mode: ThemeMode): void {
        this.themeMode = mode;
        this.saveThemePreference(mode);
        this.updateCurrentTheme();
    }

    getTheme(): Theme {
        return this.currentTheme;
    }

    getThemeMode(): ThemeMode {
        return this.themeMode;
    }

    getEffectiveMode(): 'light' | 'dark' {
        return this.themeMode === 'auto' ? this.systemPreference : this.themeMode;
    }

    getLightTheme(): Theme {
        return lightTheme;
    }

    getDarkTheme(): Theme {
        return darkTheme;
    }

    isDarkMode(): boolean {
        return this.getEffectiveMode() === 'dark';
    }

    // Listen for theme changes
    onThemeChange(callback: (theme: Theme) => void): () => void {
        this.themeChangeListeners.push(callback);
        // Immediately call with current theme
        callback(this.currentTheme);
        
        // Return unsubscribe function
        return () => {
            this.themeChangeListeners = this.themeChangeListeners.filter(l => l !== callback);
        };
    }

    // Sync theme from user profile
    syncFromUserProfile(themeMode: ThemeMode): void {
        if (themeMode !== this.themeMode) {
            this.setTheme(themeMode);
        }
    }
}

export default new ThemeService();
